import { FormatISODate } from '../../../helpers/formatting'

/**
 * Pure helpers extracted from VerificationPaymentSchedule so the branch logic is unit-testable
 * outside the component's async handlers.
 */

export function getManagerApprovalStatus(codeKey) {
  if (codeKey == 'mgr_approval_status_required') {
    return 'Awaiting Manager Approval'
  } else if (codeKey == 'mgr_approval_status_approved') {
    return 'Approved By Manager'
  } else if (codeKey == 'mgr_approval_status_declined') {
    return 'Declined By Manager'
  } else {
    return ''
  }
}

export function getFundingMessage(status, portfolioHasRealtimePayment) {
  if (portfolioHasRealtimePayment == 0) {
    return 'Real-time funding is not currently available for this portfolio.'
  } else if (status == 1) {
    return 'Eligible for real-time funding.'
  } else if (status == 0) {
    return 'This customer is not eligible for real-time funding. Please switch to ACH to continue.'
  } else {
    return ''
  }
}

/**
 * Diff the schedule the agent submitted against what the API returned, producing the
 * "System Auto-Adjustments" list. `bounds` carries the current { value } objects for
 * minPrincipal / maxPrincipal / minApr / maxApr / maxPaymentAmount.
 */
export function computeScheduleAdjustments({
  sanitizedData,
  responseData,
  dropdownOptions,
  programDetails,
  bounds,
}) {
  const { minPrincipal, maxPrincipal, minApr, maxApr, maxPaymentAmount } =
    bounds
  const adjustments = []

  if (
    sanitizedData.effective_date !==
    responseData.payment_schedule?.effective_date
  ) {
    adjustments.push({
      field: 'Effective Date',
      from: FormatISODate(sanitizedData.effective_date),
      to: FormatISODate(responseData.payment_schedule?.effective_date),
    })
  }
  if (
    String(sanitizedData.payment_method_dropdown_option_id) !==
    String(responseData.payment_method_dropdown_option_id)
  ) {
    adjustments.push({
      field: 'Payment Method',
      from: dropdownOptions.find(
        (o) => String(o?.id) === sanitizedData.payment_method_dropdown_option_id
      )?.name,
      to: dropdownOptions.find(
        (o) => o?.id === +responseData.payment_method_dropdown_option_id
      )?.name,
    })
  }
  if (
    String(sanitizedData.loan_funding_method_dropdown_option_id) !==
    String(responseData.loan_funding_method_dropdown_option_id)
  ) {
    adjustments.push({
      field: 'Loan Funding Method',
      from: dropdownOptions.find(
        (o) => o?.id === sanitizedData.loan_funding_method_dropdown_option_id
      )?.name,
      to: dropdownOptions.find(
        (o) => o?.id === +responseData.loan_funding_method_dropdown_option_id
      )?.name,
    })
  }

  const newPtiTerms = responseData.pti_terms
  if (newPtiTerms) {
    const newMinPrincipal =
      newPtiTerms.min_principal &&
      parseInt(newPtiTerms.min_principal, 10) >
        parseInt(programDetails?.min_principal || 0, 10)
        ? parseInt(newPtiTerms.min_principal, 10)
        : programDetails?.min_principal
    const newMaxPrincipal =
      newPtiTerms.max_principal &&
      parseInt(newPtiTerms.max_principal, 10) <
        parseInt(programDetails?.max_principal || Infinity, 10)
        ? parseInt(newPtiTerms.max_principal, 10)
        : programDetails?.max_principal
    if (newMinPrincipal !== minPrincipal.value) {
      adjustments.push({
        field: 'Min Principal Amount',
        from: `$${minPrincipal.value}`,
        to: `$${newMinPrincipal}`,
      })
    }
    if (newMaxPrincipal !== maxPrincipal.value) {
      adjustments.push({
        field: 'Max Principal Amount',
        from: `$${maxPrincipal.value}`,
        to: `$${newMaxPrincipal}`,
      })
    }
    const newMinApr =
      newPtiTerms.min_apr &&
      newPtiTerms.min_apr > (programDetails?.min_apr || 0)
        ? newPtiTerms.min_apr
        : programDetails?.min_apr
    const newMaxApr =
      newPtiTerms.max_apr &&
      newPtiTerms.max_apr < (programDetails?.max_apr || Infinity)
        ? newPtiTerms.max_apr
        : programDetails?.max_apr
    if (newMinApr !== minApr.value) {
      adjustments.push({
        field: 'Min APR',
        from: `${minApr.value}%`,
        to: `${newMinApr}%`,
      })
    }
    if (newMaxApr !== maxApr.value) {
      adjustments.push({
        field: 'Max APR',
        from: `${maxApr.value}%`,
        to: `${newMaxApr}%`,
      })
    }
    if (
      newPtiTerms.max_payment_amount &&
      parseInt(newPtiTerms.max_payment_amount, 10) !== maxPaymentAmount.value
    ) {
      adjustments.push({
        field: 'Max Payment Amount',
        from: `$${maxPaymentAmount.value}`,
        to: `$${parseInt(newPtiTerms.max_payment_amount, 10)}`,
      })
    }
  }

  return adjustments
}

/**
 * The offered regular payment = the first installment of the most-recently generated schedule.
 * Null when no schedule has been generated yet (the "Customer Declined" gate).
 */
export function getOfferedPaymentAmount(paymentScheduleData) {
  return paymentScheduleData?.payment_schedule?.schedule?.[0]?.payment ?? null
}

/**
 * Banding is an Operations decision — they analyse the captured data, so route any retune through
 * them. The API stores whatever band is chosen, so no value here needs a backend release.
 */
export const IDEAL_PAYMENT_AMOUNT_CONFIG = {
  bandWidth: 50,
  firstBandCeiling: 100,
  // The range below the ceiling is always broken into narrower bands rather than a single
  // `< $100` catch-all, which overlapped them and hid how far below the offer the customer was.
  smallBandWidth: 25,
}

const buildBand = (lower, width) => {
  const upper = lower + width - 1
  return { label: `$${lower} - $${upper}`, value: `${lower} - ${upper}` }
}

/**
 * Highest band first, down through the one the offered payment falls in — kept whole, so its
 * ceiling can sit above the offer. No upper limit: bands keep itemizing at `bandWidth` no matter
 * how large the offer is. Values are unformatted; only the label carries dollar signs.
 */
export function buildIdealPaymentAmountOptions(
  offeredPaymentAmount,
  {
    bandWidth,
    firstBandCeiling,
    smallBandWidth,
  } = IDEAL_PAYMENT_AMOUNT_CONFIG
) {
  const offered = Number(offeredPaymentAmount)
  if (
    !Number.isFinite(offered) ||
    offered <= 0 ||
    !(bandWidth > 0) ||
    !(firstBandCeiling > 0) ||
    !(smallBandWidth > 0)
  ) {
    return []
  }

  const options = [{ label: `< $${smallBandWidth}`, value: `< ${smallBandWidth}` }]
  for (let lower = smallBandWidth; lower < firstBandCeiling && lower <= offered; lower += smallBandWidth) {
    options.push(buildBand(lower, smallBandWidth))
  }

  for (let lower = firstBandCeiling; lower <= offered; lower += bandWidth) {
    options.push(buildBand(lower, bandWidth))
  }

  return options.reverse()
}
